import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';

const API = process.env.REACT_APP_REST_API || 'http://localhost:4000/api';

// ── Styled (mismo sistema que InvitePage / login.js) ──────────────────────────

const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f4f1e9;
  font-family: 'Avenir Next', 'Trebuchet MS', sans-serif;
  @media (max-width: 640px) { padding: 16px; }
`;

const Shell = styled.section`
  width: min(100%, 960px);
  background: #ffffff;
  border: 1px solid rgba(53, 62, 53, 0.45);
  box-shadow: 0 28px 80px rgba(38, 42, 33, 0.14);
  border-radius: 4px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 560px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const IllustrationPanel = styled.div`
  background: linear-gradient(180deg, #1e3a2f 0%, #274e35 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 36px;
  gap: 20px;
  @media (max-width: 768px) { padding: 40px 24px; min-height: 240px; }
`;

const CampIcon = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3.5rem;
  overflow: hidden;
  flex-shrink: 0;
`;

const CampLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const CampName = styled.h1`
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.4rem, 2.5vw, 1.9rem);
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
`;

const CampDate = styled.p`
  margin: 0;
  color: rgba(255,255,255,0.7);
  font-size: 0.9rem;
  text-align: center;
`;

const ProgressBar = styled.div`
  height: 3px;
  background: #e8e8e8;
  border-radius: 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #284b31;
  border-radius: 0;
  transition: width 0.3s ease;
  width: ${p => p.$pct}%;
`;

const FormPanel = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 48px 40px;
  @media (max-width: 768px) { padding: 32px 24px; }
`;

const Form = styled.form`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 1.45rem;
  font-weight: 700;
  color: #131313;
`;

const StepSubtitle = styled.p`
  margin: 0 0 24px;
  color: #8d8d8d;
  font-size: 0.9rem;
  line-height: 1.5;
`;

const Field = styled.label`
  position: relative;
  display: block;
  margin-bottom: 20px;
`;

const FieldLabel = styled.span`
  position: absolute;
  top: -10px; left: 16px;
  padding: 0 7px;
  color: #8b8b8b;
  background: #ffffff;
  font-size: 0.82rem;
  line-height: 1;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 14px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 0.95rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: #284b31;
    box-shadow: 0 0 0 3px rgba(40,75,49,0.12);
  }
  &:disabled { background: #f5f5f5; color: #888; cursor: not-allowed; }
`;

const Select = styled.select`
  width: 100%;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 0.95rem;
  outline: none;
  background: #fff;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23888' d='M0 0l6 8 6-8z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 16px center;
  transition: border-color 0.2s;
  &:focus { border-color: #284b31; box-shadow: 0 0 0 3px rgba(40,75,49,0.12); }
`;

const PrimaryButton = styled.button`
  width: 100%;
  height: 52px;
  margin-top: 4px;
  border: 0;
  border-radius: 4px;
  background: #284b31;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover:not(:disabled) { background: #203c27; }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { background: #a0b8a6; cursor: not-allowed; }
`;

const SecondaryButton = styled.button`
  width: 100%;
  height: 52px;
  margin-top: 10px;
  border: 1px solid #284b31;
  border-radius: 4px;
  background: transparent;
  color: #284b31;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: rgba(40,75,49,0.06); }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #284b31;
  font-size: 0.88rem;
  cursor: pointer;
  padding: 0;
  margin-top: 14px;
  text-align: left;
  &:hover { text-decoration: underline; }
`;

const ClubCard = styled.div`
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
  background: #f9f9f9;
`;

const ClubCardRow = styled.p`
  margin: 0 0 4px;
  font-size: 0.88rem;
  color: #555;
  &:last-child { margin: 0; }
  strong { color: #1f1f1f; }
`;

const ErrorBox = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid #efc3c3;
  border-radius: 4px;
  color: #9d2f2f;
  background: #fff3f3;
  font-size: 0.88rem;
`;

const SuccessBox = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const CheckCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #c1efd0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #1e5c30;
`;

const SuccessTitle = styled.h2`
  margin: 0;
  font-size: 1.4rem;
  font-weight: 700;
  color: #284b31;
`;

const SuccessText = styled.p`
  margin: 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.6;
`;

const BlockedIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fde8cc;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #9d4a0a;
`;

const DuplicateCard = styled(ClubCard)`
  border-color: #f5c07a;
  background: #fffbf4;
  margin-bottom: 10px;
`;

const DuplicateConfirmButton = styled(PrimaryButton)`
  height: 40px;
  margin-top: 8px;
  font-size: 0.88rem;
  background: #9d4a0a;
  &:hover:not(:disabled) { background: #7d3a08; }
`;

// ── Pasos ─────────────────────────────────────────────────────────────────────

const STEP = {
  WELCOME:            'welcome',
  EMAIL:              'email',
  EMAIL_CONFIRM:      'email_confirm',
  CLUB_FOUND:         'club_found',
  CLUB_DATA:          'club_data',
  DUPLICATE_CHECK:    'duplicate_check',
  ALREADY_REGISTERED: 'already_registered',
  PASSWORD_A:         'password_a',
  PASSWORD_B:         'password_b',
  SUCCESS:            'success',
};

const PROGRESS = {
  welcome: 10, email: 20, email_confirm: 35, club_found: 50, club_data: 50,
  duplicate_check: 60, already_registered: 60,
  password_a: 75, password_b: 75, success: 100,
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

// ── Componente ────────────────────────────────────────────────────────────────

export default function ClubInvitePage() {
  const { campId }   = useParams();
  const navigate     = useNavigate();

  const [camp, setCamp]         = useState(null);
  const [campLoading, setCampLoading] = useState(true);
  const [campError, setCampError]     = useState('');

  const [step, setStep]     = useState(STEP.WELCOME);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  // Datos compartidos entre pasos
  const [email, setEmail]             = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [existingUser, setExistingUser] = useState(null);
  const [existingClub, setExistingClub] = useState(null);
  const [affiliations, setAffiliations] = useState([]);

  // Paso 3B — datos del club
  const [clubName, setClubName]         = useState('');
  const [directorName, setDirectorName] = useState('');
  const [affiliatedTo, setAffiliatedTo] = useState('');

  // Paso 4A — contraseña existente
  const [passwordA, setPasswordA] = useState('');

  // Paso 4B — nueva contraseña
  const [passwordB, setPasswordB]   = useState('');
  const [passwordB2, setPasswordB2] = useState('');

  // Resultado exitoso
  const [result, setResult] = useState(null);

  // Duplicados detectados
  const [similarClubs, setSimilarClubs] = useState([]);

  useEffect(() => {
    axios.get(`${API}/club-invite/camp/${campId}`)
      .then(r => { setCamp(r.data); setCampLoading(false); })
      .catch(err => {
        const msg = err.response?.status === 410
          ? 'Este campamento ya no está activo.'
          : 'Campamento no encontrado.';
        setCampError(msg);
        setCampLoading(false);
      });

    axios.get(`${API}/affiliation?campId=${campId}`)
      .then(r => setAffiliations(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [campId]);

  const go = (nextStep) => { setError(''); setStep(nextStep); };

  // Paso 2 — verificar email
  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/club-invite/check-email`, { email });
      if (data.exists) {
        setExistingUser(data.user);
        setExistingClub(data.club);
        go(STEP.CLUB_FOUND);
      } else {
        setEmailConfirm('');
        go(STEP.EMAIL_CONFIRM);
      }
    } catch {
      setError('No se pudo verificar el email. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Paso EMAIL_CONFIRM — confirmar email antes de llenar datos del club
  const handleConfirmEmail = (e) => {
    e.preventDefault();
    setError('');
    if (email.toLowerCase().trim() !== emailConfirm.toLowerCase().trim()) {
      setError('Los emails no coinciden. Verifica e intenta nuevamente.');
      return;
    }
    go(STEP.CLUB_DATA);
  };

  // Paso 4A — join (director existente)
  const handleJoin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/club-invite/join`, {
        email, password: passwordA, campId,
      });
      localStorage.setItem('director_token', data.token);
      setResult(data);
      go(STEP.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.message || 'Credenciales incorrectas.');
    } finally {
      setLoading(false);
    }
  };

  // Paso CLUB_DATA → verificar duplicados antes de continuar
  const handleCheckDuplicate = async (e) => {
    e.preventDefault();
    setError('');
    if (!affiliatedTo) { setError('Debes seleccionar una afiliación.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/club-invite/check-duplicate`, {
        clubName, directorName, affiliatedTo, campId,
      });
      if (data.matches?.length > 0) {
        setSimilarClubs(data.matches);
        go(STEP.DUPLICATE_CHECK);
      } else {
        go(STEP.PASSWORD_B);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al verificar duplicados. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  // Paso 4B — register (director nuevo)
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (passwordB.length < 6) { setError('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (passwordB !== passwordB2) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/club-invite/register`, {
        email, password: passwordB, clubName, affiliatedTo,
        directorName, campId,
      });
      localStorage.setItem('director_token', data.token);
      setResult(data);
      go(STEP.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  // ── Panel izquierdo (fijo) ─────────────────────────────────────────────────
  const leftPanel = (
    <IllustrationPanel>
      <CampIcon>
        {camp?.logoUrl
          ? <CampLogo src={camp.logoUrl} alt={camp.name} />
          : <span role="img" aria-label="campamento">⛺</span>
        }
      </CampIcon>
      {campLoading
        ? <CircularProgress sx={{ color: 'rgba(255,255,255,0.6)' }} />
        : campError
          ? <CampName>{campError}</CampName>
          : <>
              <CampName>{camp?.name}</CampName>
              {camp?.dateInit && (
                <CampDate>
                  {formatDate(camp.dateInit)}
                  {camp.dateEnd ? ` — ${formatDate(camp.dateEnd)}` : ''}
                </CampDate>
              )}
            </>
      }
    </IllustrationPanel>
  );

  // ── Panel derecho según paso ───────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {

      case STEP.WELCOME:
        return (
          <StepContent>
            <Form onSubmit={e => { e.preventDefault(); go(STEP.EMAIL); }}>
              <StepTitle>Tu club fue invitado</StepTitle>
              <StepSubtitle>
                {camp ? `Registra tu club en "${camp.name}".` : 'Acepta la invitación para inscribir tu club.'}
              </StepSubtitle>
              <PrimaryButton type="submit" disabled={campLoading || !!campError}>
                Registrar mi club
              </PrimaryButton>
              <BackLink type="button" onClick={() => navigate('/director/login')}>
                Ya tengo cuenta — ir al dashboard
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.EMAIL:
        return (
          <StepContent>
            <Form onSubmit={handleCheckEmail}>
              <StepTitle>Email del director</StepTitle>
              <StepSubtitle>Ingresa el email con el que gestionarás tu club.</StepSubtitle>
              {error && <ErrorBox>{error}</ErrorBox>}
              <Field>
                <FieldLabel>Email</FieldLabel>
                <Input
                  type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  required autoFocus
                />
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Continuar'}
              </PrimaryButton>
              <BackLink type="button" onClick={() => go(STEP.WELCOME)}>
                ← Volver
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.EMAIL_CONFIRM:
        return (
          <StepContent>
            <Form onSubmit={handleConfirmEmail}>
              <StepTitle>Confirma tu email</StepTitle>
              <StepSubtitle>
                Escribe nuevamente tu email para confirmar que es correcto.
              </StepSubtitle>
              {error && <ErrorBox>{error}</ErrorBox>}
              <Field>
                <FieldLabel>Email</FieldLabel>
                <Input type="email" value={email} disabled />
              </Field>
              <Field>
                <FieldLabel>Confirmar email</FieldLabel>
                <Input
                  type="email" value={emailConfirm}
                  onChange={e => setEmailConfirm(e.target.value)}
                  required autoFocus
                  onPaste={e => e.preventDefault()}
                />
              </Field>
              <PrimaryButton type="submit">Continuar</PrimaryButton>
              <BackLink type="button" onClick={() => go(STEP.EMAIL)}>
                ← Cambiar email
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.CLUB_FOUND:
        return (
          <StepContent>
            <Form onSubmit={e => { e.preventDefault(); go(STEP.PASSWORD_A); }}>
              <StepTitle>Club encontrado</StepTitle>
              <StepSubtitle>Encontramos un club asociado a este email.</StepSubtitle>
              {existingClub && (
                <ClubCard>
                  <ClubCardRow><strong>{existingClub.name}</strong></ClubCardRow>
                  {existingClub.affiliatedTo && (
                    <ClubCardRow>Afiliación: <strong>{existingClub.affiliatedTo.name}</strong></ClubCardRow>
                  )}
                  {existingUser && (
                    <ClubCardRow>Director: <strong>{existingUser.fullname}</strong></ClubCardRow>
                  )}
                </ClubCard>
              )}
              <PrimaryButton type="submit">
                Sí, es mi club — continuar
              </PrimaryButton>
              <SecondaryButton type="button" onClick={() => go(STEP.CLUB_DATA)}>
                Actualizar datos del club
              </SecondaryButton>
              <BackLink type="button" onClick={() => go(STEP.EMAIL)}>
                ← Usar otro email
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.CLUB_DATA:
        return (
          <StepContent>
            <Form onSubmit={handleCheckDuplicate}>
              <StepTitle>Datos del club</StepTitle>
              <StepSubtitle>Completa la información de tu club.</StepSubtitle>
              {error && <ErrorBox>{error}</ErrorBox>}
              <Field>
                <FieldLabel>Nombre del club</FieldLabel>
                <Input
                  type="text" value={clubName}
                  onChange={e => setClubName(e.target.value)}
                  required autoFocus
                />
              </Field>
              <Field>
                <FieldLabel>Afiliación *</FieldLabel>
                <Select value={affiliatedTo} onChange={e => setAffiliatedTo(e.target.value)} required>
                  <option value="" disabled>Selecciona una afiliación</option>
                  {affiliations.map(a => (
                    <option key={a._id} value={a._id}>{a.name}{a.zone ? ` — Zona ${a.zone}` : ''}</option>
                  ))}
                </Select>
              </Field>
              <Field>
                <FieldLabel>Nombre del director</FieldLabel>
                <Input
                  type="text" value={directorName}
                  onChange={e => setDirectorName(e.target.value)}
                  required
                />
              </Field>
              <Field>
                <FieldLabel>Email</FieldLabel>
                <Input type="email" value={email} disabled />
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Continuar'}
              </PrimaryButton>
              <BackLink type="button" onClick={() => go(STEP.EMAIL)}>
                ← Cambiar email
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.DUPLICATE_CHECK:
        return (
          <StepContent>
            <Form onSubmit={e => e.preventDefault()}>
              <StepTitle>¿Ya registraste tu club?</StepTitle>
              <StepSubtitle>
                {similarClubs.length === 1
                  ? 'Encontramos un club similar al tuyo ya inscrito en este campamento. ¿Es este tu club?'
                  : `Encontramos ${similarClubs.length} clubs similares al tuyo. ¿Alguno de estos es tu club?`}
              </StepSubtitle>
              {similarClubs.map(club => (
                <div key={club._id}>
                  <DuplicateCard>
                    <ClubCardRow><strong>{club.name}</strong></ClubCardRow>
                    {club.affiliatedTo && (
                      <ClubCardRow>
                        Afiliación: <strong>
                          {club.affiliatedTo.name}
                          {club.affiliatedTo.zone ? ` — Zona ${club.affiliatedTo.zone}` : ''}
                        </strong>
                      </ClubCardRow>
                    )}
                    {club.director && (
                      <ClubCardRow>Director: <strong>{club.director}</strong></ClubCardRow>
                    )}
                  </DuplicateCard>
                  <DuplicateConfirmButton type="button" onClick={() => go(STEP.ALREADY_REGISTERED)}>
                    Sí, es mi club
                  </DuplicateConfirmButton>
                </div>
              ))}
              <SecondaryButton type="button" style={{ marginTop: 14 }} onClick={() => go(STEP.PASSWORD_B)}>
                {similarClubs.length > 1 ? 'Ninguno es mi club' : 'No, no es mi club'}
              </SecondaryButton>
              <BackLink type="button" onClick={() => go(STEP.CLUB_DATA)}>← Volver</BackLink>
            </Form>
          </StepContent>
        );

      case STEP.ALREADY_REGISTERED:
        return (
          <StepContent>
            <SuccessBox>
              <BlockedIcon aria-hidden="true">⚠</BlockedIcon>
              <SuccessTitle style={{ color: '#9d4a0a' }}>Club ya inscrito</SuccessTitle>
              <SuccessText>
                Tu club ya está inscrito en este campamento.<br />
                Comunícate con tu administrador o coordinador para obtener acceso.
              </SuccessText>
              <SecondaryButton type="button" style={{ marginTop: 8 }} onClick={() => go(STEP.WELCOME)}>
                Volver al inicio
              </SecondaryButton>
            </SuccessBox>
          </StepContent>
        );

      case STEP.PASSWORD_A:
        return (
          <StepContent>
            <Form onSubmit={handleJoin}>
              <StepTitle>Confirmar identidad</StepTitle>
              <StepSubtitle>Ingresa tu contraseña para unirte al campamento.</StepSubtitle>
              {error && <ErrorBox>{error}</ErrorBox>}
              <Field>
                <FieldLabel>Email</FieldLabel>
                <Input type="email" value={email} disabled />
              </Field>
              <Field>
                <FieldLabel>Contraseña</FieldLabel>
                <Input
                  type="password" value={passwordA}
                  onChange={e => setPasswordA(e.target.value)}
                  required autoFocus
                />
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Unirme al campamento'}
              </PrimaryButton>
              <BackLink type="button" onClick={() => navigate(`/director/login`)}>
                ¿Olvidaste tu contraseña?
              </BackLink>
              <BackLink type="button" onClick={() => go(STEP.CLUB_FOUND)}>
                ← Volver
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.PASSWORD_B:
        return (
          <StepContent>
            <Form onSubmit={handleRegister}>
              <StepTitle>Crear contraseña</StepTitle>
              <StepSubtitle>Elige una contraseña para tu cuenta de director.</StepSubtitle>
              {error && <ErrorBox>{error}</ErrorBox>}
              <Field>
                <FieldLabel>Contraseña</FieldLabel>
                <Input
                  type="password" value={passwordB}
                  onChange={e => setPasswordB(e.target.value)}
                  minLength={6} required autoFocus
                />
              </Field>
              <Field>
                <FieldLabel>Confirmar contraseña</FieldLabel>
                <Input
                  type="password" value={passwordB2}
                  onChange={e => setPasswordB2(e.target.value)}
                  minLength={6} required
                />
              </Field>
              <PrimaryButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Crear cuenta y unirme'}
              </PrimaryButton>
              <BackLink type="button" onClick={() => go(STEP.CLUB_DATA)}>
                ← Volver
              </BackLink>
            </Form>
          </StepContent>
        );

      case STEP.SUCCESS:
        return (
          <StepContent>
            <SuccessBox>
              <CheckCircle aria-hidden="true">✓</CheckCircle>
              <SuccessTitle>Club registrado</SuccessTitle>
              <SuccessText>
                <strong>{result?.club?.name || 'Tu club'}</strong> fue inscrito en{' '}
                <strong>{result?.camp?.name || camp?.name}</strong>.<br />
                Ya puedes gestionar tu club desde el dashboard.
              </SuccessText>
              <PrimaryButton
                type="button"
                style={{ marginTop: 8 }}
                onClick={() => navigate('/director/dashboard')}
              >
                Ir al dashboard
              </PrimaryButton>
            </SuccessBox>
          </StepContent>
        );

      default:
        return null;
    }
  };

  return (
    <Root>
      <Shell>
        {leftPanel}
        <FormPanel>
          <ProgressBar>
            <ProgressFill $pct={PROGRESS[step] || 10} />
          </ProgressBar>
          {renderStep()}
        </FormPanel>
      </Shell>
    </Root>
  );
}
